import { Card } from '@payaca/ambrosia';
import { FC, type JSX } from 'react';

import BasicField from '@payaca/components/basicField/BasicField';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import SwitchField from '@payaca/components/switchField/SwitchField';
import TextareaFieldFormatter, {
  ToolbarColourVariant,
} from '@payaca/components/textareaField/TextareaFieldFormatter';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';

import { useBusinessAccountForm } from './useBusinessAccountForm';
import { useCompanyInfoFormValidators } from '@/hooks/useCompanyInfoValidators';
import { useAccount } from '@/api/rest/users/useAccount';
import SettingsSaveButton from './SettingsSaveButton';

type Props = {
  readOnly: boolean;
};

type FormState = {
  propositionValidForDays: number | null;
  invoiceDueInDays: number | null;
  proposalPaymentTerms: string;
  invoicePaymentTerms: string;
  hideItemPrices: boolean;
  hideVatBreakdown: boolean;
  defaultDepositPercentage: number | null;
};

const CompanySettingsProposalsInvoices: FC<Props> = ({
  readOnly,
}: Props): JSX.Element => {
  const account = useAccount();

  const initialState: FormState = {
    propositionValidForDays: account.propositionValidForDays,
    invoiceDueInDays: account.invoiceDueInDays,
    proposalPaymentTerms: account.proposalPaymentTerms ?? '',
    invoicePaymentTerms: account.invoicePaymentTerms ?? '',
    hideItemPrices: account.hideItemPrices ?? false,
    hideVatBreakdown: account.hideVatBreakdown ?? false,
    defaultDepositPercentage: account.defaultDepositPercentage ?? 0,
  };

  const fieldValidators = useCompanyInfoFormValidators([]);

  const {
    formState,
    updateFormFields,
    formValidationResult,
    saveAndFlush,
    isValid,
    isDirty,
    isSaving,
  } = useBusinessAccountForm<FormState>(initialState, fieldValidators);

  return (
    <>
      <Card sizeVariant="lg">
        <Card.Body className="flex flex-col gap-2">
          <p>
            Default behaviour for number of days a proposal is valid for and
            invoice is due until. These can be changed on a per job basis.
          </p>
          <ValidatedFieldWrapper
            validationResult={formValidationResult.propositionValidForDays}
          >
            <BasicField
              label={'Default days proposal is valid for'}
              name="propositionValidForDays"
              value={formState.propositionValidForDays}
              type="number"
              isDisabled={readOnly}
              onChange={(value: {
                [propositionValidForDays: string]: string;
              }) => {
                if (
                  Number(value.propositionValidForDays) &&
                  value.propositionValidForDays !== ''
                ) {
                  updateFormFields({
                    propositionValidForDays: Math.round(
                      Number(value.propositionValidForDays)
                    ),
                  });
                } else {
                  updateFormFields({
                    propositionValidForDays: null,
                  });
                }
              }}
            />
          </ValidatedFieldWrapper>
          <ValidatedFieldWrapper
            validationResult={formValidationResult.invoiceDueInDays}
          >
            <BasicField
              label={'Default days invoice is due in'}
              name="invoiceDueInDays"
              value={formState.invoiceDueInDays}
              type="number"
              isDisabled={readOnly}
              onChange={(value: { [invoiceDueInDays: string]: string }) => {
                if (
                  Number(value.invoiceDueInDays) >= 0 &&
                  value.invoiceDueInDays !== ''
                ) {
                  updateFormFields({
                    invoiceDueInDays: Math.round(
                      Number(value.invoiceDueInDays)
                    ),
                  });
                } else {
                  updateFormFields({
                    invoiceDueInDays: null,
                  });
                }
              }}
            />
          </ValidatedFieldWrapper>
          <ValidatedFieldWrapper
            validationResult={formValidationResult.proposalPaymentTerms}
          >
            <TextareaFieldFormatter
              label="Payment terms shown on proposals"
              value={formState.proposalPaymentTerms}
              name="proposalPaymentTerms"
              onChange={updateFormFields}
              isDisabled={readOnly}
              placeholder="You can describe additional payment terms here. This will be added to the bottom of your proposals."
              toolbarColourVariant={ToolbarColourVariant.SECONDARY}
            />
          </ValidatedFieldWrapper>
          <ValidatedFieldWrapper
            validationResult={formValidationResult.invoicePaymentTerms}
          >
            <TextareaFieldFormatter
              label="Payment terms shown on invoices"
              value={formState.invoicePaymentTerms}
              name="invoicePaymentTerms"
              onChange={updateFormFields}
              isDisabled={readOnly}
              placeholder="You can describe additional payment terms here. This will be added to the bottom of your invoices."
              toolbarColourVariant={ToolbarColourVariant.SECONDARY}
            />
          </ValidatedFieldWrapper>
        </Card.Body>
      </Card>

      <Card sizeVariant="lg">
        <Card.Body className="flex flex-col gap-2">
          <h2>Project defaults</h2>
          <div>
            <SwitchField
              name="hideItemPrices"
              value={formState.hideItemPrices}
              onChange={updateFormFields}
              label="Hide item prices"
              isDisabled={readOnly}
            />
            <p>
              Default behaviour for hiding item prices on your quotes, estimates
              and invoices. This can be changed on a per-project basis.
            </p>
          </div>
          <div>
            <SwitchField
              name="hideVatBreakdown"
              value={formState.hideVatBreakdown}
              onChange={updateFormFields}
              label={`Hide tax breakdown`}
              isDisabled={readOnly}
            />
            <p>
              Default behaviour for hiding tax values on your quotes, estimates
              and change proposals. This can be changed on a per-project basis.
              Invoices will not be affected by this setting and will always show
              the tax breakdown.
            </p>
          </div>
          <div className="default-deposit-percentage-wrapper">
            <BasicField
              styleVariant={InputStyleVariant.OUTSIZE}
              value={formState.defaultDepositPercentage}
              name={'defaultDepositPercentage'}
              isDisabled={readOnly}
              onChange={(value: { [key: string]: any }) => {
                updateFormFields({
                  defaultDepositPercentage: value.defaultDepositPercentage
                    ? Math.abs(
                        Math.round(value.defaultDepositPercentage * 100) / 100
                      )
                    : value.defaultDepositPercentage,
                });
              }}
              type="number"
              additionalInputProps={{
                pattern: '[0-9]*',
                min: 0,
                max: 100,
                step: 1,
              }}
              iconAfter={'percent-02.3'}
              label="Default deposit percentage"
            />
          </div>
        </Card.Body>
      </Card>
      {!readOnly && (
        <SettingsSaveButton
          isDirty={isDirty}
          isSaving={isSaving}
          isValid={isValid}
          onSave={saveAndFlush}
        />
      )}
    </>
  );
};

export default CompanySettingsProposalsInvoices;
